import csMessages from '../locales/cs.json';
import enMessages from '../locales/en.json';

export type TranslationParams = Record<string, string | number | null | undefined>;
export const supportedLocales = [
  { code: 'cs', label: 'Čeština (CS)', name: 'Čeština' },
  { code: 'en', label: 'English (EN)', name: 'English' },
] as const;
export type Locale = (typeof supportedLocales)[number]['code'];

const localeStorageKey = 'atomfit.locale';

const fallbackMessages: Record<string, string> = csMessages;
const messagesByLocale: Record<Locale, Record<string, string>> = {
  cs: csMessages,
  en: enMessages,
};

export function resolveLocale(value: string | null | undefined): Locale {
  const language = value?.trim().toLowerCase().split(/[-_]/, 1)[0];
  return (
    supportedLocales.find((locale) => locale.code === (language === 'sk' ? 'cs' : language))
      ?.code ?? 'en'
  );
}

function getStoredLocale(): Locale | null {
  try {
    const storedLocale = window.localStorage.getItem(localeStorageKey);
    return supportedLocales.find((locale) => locale.code === storedLocale)?.code ?? null;
  } catch {
    return null;
  }
}

const activeLocale = getStoredLocale() ?? resolveLocale(window.navigator.language);
const messages = messagesByLocale[activeLocale];

export function getLocale(): Locale {
  return activeLocale;
}

export function setLocale(locale: Locale): void {
  if (locale === activeLocale) return;

  try {
    window.localStorage.setItem(localeStorageKey, locale);
  } catch {
    // The page still reloads gracefully when storage is unavailable.
  }

  document.documentElement.lang = locale;
  window.location.reload();
}

/**
 * Resolves a language-neutral message key from the configured catalog.
 *
 * Interpolations use `{{name}}` placeholders so translators can reorder dynamic
 * values without changing application code.
 */
export function translate(key: string, params: TranslationParams = {}): string {
  const message = messages[key] ?? fallbackMessages[key] ?? key;

  return message.replace(/\{\{(\w+)\}\}/g, (placeholder, name: string) => {
    const value = params[name];
    return value === null || value === undefined ? placeholder : String(value);
  });
}
